import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import Typography from 'material-ui/Typography';
import TextField from 'material-ui/TextField';
import classNames from 'classnames';
import DialogModal from '../../common/DialogModal';
import Button from '../../Parts/Button';

const MIN_REQUIRED_CSV_LINES = 2;
const PROGRESS_PERCENT_MAX = 100;

const styles = () => ({
  paper: {
    maxHeight: 'unset',
    background: '#F4F6F9',
    width: '960px',
  },

  contentContainer: {
    margin: '0px',
    padding: '20px 25px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    textAlign: 'left',
    boxSizing: 'border-box',
  },

  container: {
    background: '#FFFFFF',
    border: '1px solid #D8DBDF',
    padding: '20px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },

  sectionTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    background: '#F4F6F9',
    padding: '10px',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '16px',
  },

  sectionTitleFirst: {
    marginTop: '0px',
  },

  caption: {
    marginTop: '6px',
    lineHeight: '20px',
    fontSize: '13px',
    color: '#71737B',
  },

  csvGuide: {
    marginTop: '8px',
    width: '100%',
    boxSizing: 'border-box',
    padding: '0px',
  },

  csvGuideHead: {
    fontSize: '12px',
    color: '#71737B',
    lineHeight: '20px',
    margin: '0',
  },

  csvSampleTable: {
    marginTop: '8px',
    width: '100%',
    borderCollapse: 'collapse',
    background: '#FFFFFF',
  },

  csvSampleTh: {
    fontSize: '11px',
    color: '#71737B',
    background: '#ECEEF0',
    border: '1px solid #D8DBDF',
    padding: '6px 8px',
    textAlign: 'left',
  },

  csvSampleTd: {
    fontSize: '11px',
    color: '#181819',
    border: '1px solid #E7E9EB',
    padding: '6px 8px',
  },

  csvGuideDesc: {
    margin: '12px 0 0 0',
    fontSize: '13px',
    lineHeight: '20px',
    color: '#71737B',
  },

  csvGuidePoint: {
    margin: '0',
    marginTop: '8px',
    fontSize: '13px',
    lineHeight: '20px',
    color: '#71737B',
  },

  row: {
    marginTop: '16px',
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'row',
    justifyContent: 'center',
  },

  left: {
    width: 'calc(50% - 10px)',
    marginRight: '20px',
  },

  right: {
    width: 'calc(50% - 10px)',
  },

  fullWidth: {
    width: '100%',
  },

  input: {
    boxSizing: 'border-box',
    '& label': {
      fontSize: '13px',
      color: '#181819',
      fontWeight: 'bold',
    },
    '& input': {
      border: '1px solid #D8DBDF',
      padding: '10px 12px',
      fontSize: '14px',
      background: '#FFFFFF',
      borderRadius: '2px',
    },
    '& input::placeholder': {
      color: '#B1B3B9',
      opacity: 1,
    },
    '& p': {
      marginTop: '4px',
      color: '#71737B',
      fontSize: '11px',
    },
  },

  downloadTemplate: {
    display: 'inline-flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '14px',
    marginTop: '8px',
  },

  downloadTemplateSpacing: {
    marginTop: '10px',
  },

  hidden: {
    display: 'none',
  },

  buttonContainer: {
    marginTop: '24px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },

  progressContainer: {
    marginTop: '16px',
    width: '100%',
  },

  progressText: {
    fontSize: '12px',
    color: '#71737B',
    marginBottom: '6px',
  },

  progressMain: {
    fontSize: '18px',
    lineHeight: '22px',
    color: '#181819',
    fontWeight: 'bold',
    marginBottom: '8px',
  },

  progressNotice: {
    marginTop: '8px',
    fontSize: '12px',
    lineHeight: '18px',
    color: '#181819',
  },

  progressTrack: {
    width: '100%',
    height: '8px',
    background: '#E7E9EB',
    borderRadius: '4px',
    overflow: 'hidden',
  },

  progressFill: {
    height: '100%',
    background: '#1A65FF',
    borderRadius: '4px',
    transition: 'width 240ms linear',
  },

  summaryRow: {
    marginTop: '8px',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },

  summaryItem: {
    fontSize: '12px',
    color: '#71737B',
  },

});

class UploadCsvDialog extends React.Component {
  state = {
    industry_major: '',
    employees: '',
    capital: '',
    sales: '',
    listing: '',
    preset: '',
  };

  handleSelectFile = () => {
    if (this.fileUpload) {
      this.fileUpload.click();
    }
  };

  handleChangeOption = (key) => (event) => {
    this.setState({ [key]: event.target.value });
  };

  buildOptions = () => ({
    industry_major: this.state.industry_major,
    employees: this.state.employees,
    capital: this.state.capital,
    sales: this.state.sales,
    listing: this.state.listing,
    preset: this.state.preset,
  });

  hasAnyOptionalOption = (options) => {
    if (!options) {
      return false;
    }

    return Object.keys(options).some((key) => {
      const value = options[key];
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  };

  readCsvText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('csv_read_error'));
      reader.readAsText(file, 'utf-8');
    });
  };

  parseCsvLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];

      if (char === '"') {
        const nextChar = line[i + 1];
        if (inQuotes && nextChar === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current);
    return values.map((value) => String(value || '').trim());
  };

  hasAddressInCsvText = (csvText) => {
    const normalizedText = String(csvText || '').replace(/^\uFEFF/, '');
    const lines = normalizedText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line !== '');

    if (lines.length < MIN_REQUIRED_CSV_LINES) {
      return false;
    }

    const header = this.parseCsvLine(lines[0]);
    const addressIndex = header.findIndex((columnName) => String(columnName).includes('住所'));
    if (addressIndex < 0) {
      return false;
    }

    for (let i = 1; i < lines.length; i += 1) {
      const row = this.parseCsvLine(lines[i]);
      const addressValue = row[addressIndex];
      if (addressValue !== null && addressValue !== undefined && String(addressValue).trim() !== '') {
        return true;
      }
    }

    return false;
  };

  validateCsvRequirements = async (file, options) => {
    if (this.hasAnyOptionalOption(options)) {
      return '';
    }

    const csvText = await this.readCsvText(file);
    if (this.hasAddressInCsvText(csvText)) {
      return '';
    }

    return '住所が未入力の場合は、任意条件を1つ以上入力してください。';
  };

  handleChangeFile = async (event) => {
    const inputTarget = event && event.target ? event.target : null;
    const files = inputTarget && inputTarget.files ? inputTarget.files : null;
    const selectedFile = files && files[0] ? files[0] : null;
    const options = this.buildOptions();

    if (selectedFile) {
      try {
        const validationError = await this.validateCsvRequirements(selectedFile, options);
        if (validationError) {
          if (this.props.onValidationError) {
            this.props.onValidationError(validationError);
          }
          if (inputTarget) {
            inputTarget.value = '';
          }
          return;
        }
      } catch (error) {
        if (this.props.onValidationError) {
          this.props.onValidationError('CSVの読み込みに失敗しました。ファイル内容を確認してください。');
        }
        if (inputTarget) {
          inputTarget.value = '';
        }
        return;
      }

      this.props.onSelectFile(selectedFile, options);
    }

    if (inputTarget) {
      inputTarget.value = '';
    }
  };

  getSummaryCount = (summary, ...keys) => {
    if (!summary) {
      return 0;
    }

    for (let index = 0; index < keys.length; index += 1) {
      const value = summary[keys[index]];
      if (value !== null && value !== undefined && value !== '') {
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? 0 : numericValue;
      }
    }

    return 0;
  };

  render() {
    const {
      classes,
      isOpen,
      toggleDialog,
      onDownloadTemplate,
      isUploading,
      uploadStatus,
      progressPercent,
      uploadSummary,
    } = this.props;
    const normalizedProgress =
      progressPercent === null || progressPercent === undefined || Number.isNaN(Number(progressPercent))
        ? null
        : Math.max(0, Math.min(PROGRESS_PERCENT_MAX, Number(progressPercent)));
    const statusLabelMap = {
      queued: '受付済み',
      processing: '処理中',
      completed: '完了',
      failed: '失敗',
    };
    const statusLabel = statusLabelMap[uploadStatus] || '処理中';
    const totalCount = this.getSummaryCount(uploadSummary, 'total_count', 'line_count');
    const processedCount = this.getSummaryCount(uploadSummary, 'processed_count');

    return (
      <DialogModal
        open={isOpen}
        onClose={toggleDialog}
        maxWidth={false}
        classes={{
          paper: classes.paper,
          contentContainer: classes.contentContainer,
        }}
        title="法人番号AI付与"
      >
        <div className={classes.container}>
          <div className={classNames(classes.sectionTitle, classes.sectionTitleFirst)}>アップロードするCSVについて</div>
          <div className={classes.csvGuide}>
            <Typography className={classes.csvGuideHead}>入力イメージ（例）</Typography>
            <table className={classes.csvSampleTable}>
              <thead>
                <tr>
                  <th className={classes.csvSampleTh}>会社名</th>
                  <th className={classes.csvSampleTh}>住所（任意）</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={classes.csvSampleTd}>株式会社サンプル</td>
                  <td className={classes.csvSampleTd}>東京都千代田区1-1-1</td>
                </tr>
                <tr>
                  <td className={classes.csvSampleTd}>テスト工業株式会社</td>
                  <td className={classes.csvSampleTd}>大阪府大阪市北区2-2-2</td>
                </tr>
              </tbody>
            </table>
            <Typography className={classes.csvGuidePoint}>
              ターゲット企業に対して法人番号を一括で付与します。データには
              <b>「会社名」という見出しの列を含ませてください</b>。
              住所列は任意ですが、含まれていると照合精度が向上します。<br />
              会社名のみでも付与は可能ですが、表記ゆれなどの理由により名寄せ精度が下がる場合があります。あらかじめご了承ください。
              <br />
              下部の「CSVを選択」からファイルをアップロードしてください。
            </Typography>
          </div>
          <div className={classNames(classes.row, classes.downloadTemplateSpacing)}>
            <div className={classes.fullWidth}>
              <div className={classes.downloadTemplate} onClick={onDownloadTemplate}>
                <img src={require('../../../assets/images/download.svg')} alt="専用ひな形をダウンロード" width="12.5px" />
                <Typography variant="body1" style={{ color: '#1A65FF', margin: '0 0 0 4px', fontSize: '12px' }}>専用ひな形をダウンロード</Typography>
              </div>
            </div>
          </div>

          <div className={classes.sectionTitle}>任意条件（指定しなくても実行できます）</div>

          <div className={classes.row}>
            <TextField
              label="業種大分類"
              value={this.state.industry_major}
              onChange={this.handleChangeOption('industry_major')}
              className={classNames(classes.input, classes.left)}
              placeholder="例: IT・通信 / 製造 / 建設"
              InputProps={{ disableUnderline: true, disabled: isUploading }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="従業員数"
              value={this.state.employees}
              onChange={this.handleChangeOption('employees')}
              className={classNames(classes.input, classes.right)}
              placeholder="例: 100名以上 / 300〜1000名"
              InputProps={{ disableUnderline: true, disabled: isUploading }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>

          <div className={classes.row}>
            <TextField
              label="資本金"
              value={this.state.capital}
              onChange={this.handleChangeOption('capital')}
              className={classNames(classes.input, classes.left)}
              placeholder="例: 1億円以上 / 5,000万円以上"
              InputProps={{ disableUnderline: true, disabled: isUploading }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="売上高"
              value={this.state.sales}
              onChange={this.handleChangeOption('sales')}
              className={classNames(classes.input, classes.right)}
              placeholder="例: 100億以上 / 10億〜50億"
              InputProps={{ disableUnderline: true, disabled: isUploading }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>

          <div className={classes.row}>
            <TextField
              label="上場区分"
              value={this.state.listing}
              onChange={this.handleChangeOption('listing')}
              className={classNames(classes.input, classes.left)}
              placeholder="例: 上場 / 非上場 / 東証プライム"
              InputProps={{ disableUnderline: true, disabled: isUploading }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <div className={classes.right}></div>
          </div>

          <div className={classes.row}>
            <TextField
              label="その他条件"
              value={this.state.preset}
              onChange={this.handleChangeOption('preset')}
              className={classNames(classes.input, classes.fullWidth)}
              placeholder="例: 本社所在地が東京 / BtoB企業優先"
              InputProps={{ disableUnderline: true, disabled: isUploading }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>

          <div className={classes.buttonContainer}>
            <Button
              text={isUploading ? 'アップロード中...' : 'CSVを選択'}
              handleClickButton={this.handleSelectFile}
              loading={isUploading}
            />
          </div>

          {isUploading && (
            <div className={classes.progressContainer}>
              <Typography className={classes.progressText}>
                {normalizedProgress === null
                  ? `処理状況: ${statusLabel}`
                  : `処理状況: ${statusLabel}（${Math.round(normalizedProgress)}%）`}
              </Typography>
              {(uploadStatus === 'queued' || uploadStatus === 'processing') && totalCount > 0 && (
                <Typography className={classes.progressMain}>
                  {processedCount} / {totalCount}件
                </Typography>
              )}
              <div className={classes.progressTrack}>
                <div className={classes.progressFill} style={{ width: `${normalizedProgress === null ? 0 : normalizedProgress}%` }} />
              </div>
              <div className={classes.summaryRow}>
                <Typography className={classes.summaryItem}>総件数: {totalCount}件</Typography>
                <Typography className={classes.summaryItem}>処理済: {processedCount}件</Typography>
                <Typography className={classes.summaryItem}>付与: {this.getSummaryCount(uploadSummary, 'assigned_count')}件</Typography>
                <Typography className={classes.summaryItem}>スキップ: {this.getSummaryCount(uploadSummary, 'skipped_count')}件</Typography>
              </div>
              <Typography className={classes.progressNotice}>
                <b>このまま他の画面へ遷移しても処理は継続され、戻ると進捗・結果を確認できます。</b>
              </Typography>
            </div>
          )}

          <input
            type="file"
            accept=".csv,text/csv"
            className={classNames(classes.hidden)}
            ref={(fileUpload) => { this.fileUpload = fileUpload; }}
            onChange={this.handleChangeFile}
          />
        </div>
      </DialogModal>
    );
  }
}

UploadCsvDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleDialog: PropTypes.func.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  onDownloadTemplate: PropTypes.func.isRequired,
  onValidationError: PropTypes.func,
  isUploading: PropTypes.bool,
  uploadStatus: PropTypes.string,
  progressPercent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  uploadSummary: PropTypes.object,
};

UploadCsvDialog.defaultProps = {
  isUploading: false,
  uploadStatus: 'queued',
  progressPercent: null,
  uploadSummary: null,
  onValidationError: null,
};

export default withStyles(styles)(UploadCsvDialog);



// WEBPACK FOOTER //
// ./src/components/CorporateNumberAssign/dialogs/UploadCsvDialog.jsx